import * as _ from "lodash"
import * as PIXI from "pixi.js"
import { type HexColor, type ID, type Idea, ideaHeight, ideaWidth } from "../model/types"
import { BOX_WIDTH } from "./grid"
import { defaultIdeaColourResolver, type IdeaColourResolver } from "./idea_colour"

PIXI.BitmapFont.from("Black", {
  // fontFamily: "Inter var",
  fill: 0x000000,
  fontWeight: "normal",
})

PIXI.BitmapFont.from("White", {
  // fontFamily: "Inter var",
  fill: 0xffffff,
  fontWeight: "normal",
})

const BITMAP_TEXT_COMPATIBLE = /^[a-zA-Z0-9 ]*$/
const FULL_TITLE_FONT_SIZE = 5
const FULL_TITLE_MIN_FONT_SIZE = 3
const FULL_TITLE_PADDING = 2
const FULL_TITLE_LINE_HEIGHT_MULTIPLIER = 1.2
const FULL_TITLE_ELLIPSIS = "..."
const FULL_TITLE_MEMO_FONT_SIZE = 4
const FULL_TITLE_MEMO_PADDING = 2
const FULL_TITLE_MARKER_TITLE_GAP = 1
const FULL_TITLE_VERTICAL_BIAS_BELOW_MARKER = 0.4
const COMPACT_TEXT_PADDING = 2

type IdeaTextColour = {
  font_name: "White" | "Black"
  fill: 0xffffff | 0x000000
}

const idea_text_content = (idea: Idea): string | undefined => idea.memo || idea.title?.slice(0, 3)

const full_title_content = (idea: Idea): string | undefined => {
  const content = (idea.title || idea.memo || "").replace(/\s+/g, " ").trim()
  return content || undefined
}

const memo_marker_content = (idea: Idea): string | undefined => {
  const content = (idea_text_content(idea) || "").trim()
  return content || undefined
}

const idea_text_font_size = (idea: Idea, content: string): number => {
  const length = _.split(content, "").length

  switch (idea.type) {
    case "idea":
      return length < 2 ? 24 : length < 3 ? 20 : 14
    case "goal":
      return length < 2 ? 42 : length < 3 ? 24 : length < 4 ? 20 : 14
    default:
      throw "renderIdeaTextToGraphic unknown idea type " + idea.type
  }
}

const idea_text_colour = (idea: Idea, ideaColourResolver: IdeaColourResolver): IdeaTextColour => {
  if (text_should_be_white_or_blank(ideaColourResolver(idea)))
    return { font_name: "White", fill: 0xffffff }

  return { font_name: "Black", fill: 0x000000 }
}

const create_idea_text = (
  idea: Idea,
  content: string,
  ideaColourResolver: IdeaColourResolver,
): PIXI.BitmapText | PIXI.Text => {
  const font_size = idea_text_font_size(idea, content)
  const colour = idea_text_colour(idea, ideaColourResolver)

  if (BITMAP_TEXT_COMPATIBLE.test(content)) {
    return new PIXI.BitmapText(content, {
      fontName: colour.font_name,
      fontSize: font_size,
      align: "center",
    })
  }

  return new PIXI.Text(content, {
    fontSize: font_size,
    fill: colour.fill,
    align: "center",
  })
}

const fit_idea_text_to_cell = (idea: Idea, text: PIXI.BitmapText | PIXI.Text): void => {
  const max_width = BOX_WIDTH * ideaWidth(idea) - COMPACT_TEXT_PADDING * 2
  const max_height = BOX_WIDTH * ideaHeight(idea) - COMPACT_TEXT_PADDING * 2
  if (text.width <= 0 || text.height <= 0 || max_width <= 0 || max_height <= 0) return

  const scale = Math.min(1, max_width / text.width, max_height / text.height)
  text.scale.set(scale)
}

const full_title_text_style = (
  idea: Idea,
  font_size: number,
  ideaColourResolver: IdeaColourResolver,
  max_width: number,
): PIXI.TextStyle =>
  new PIXI.TextStyle({
    fontSize: font_size,
    fill: idea_text_colour(idea, ideaColourResolver).fill,
    align: "center",
    breakWords: true,
    wordWrap: true,
    wordWrapWidth: max_width,
    lineHeight: font_size * FULL_TITLE_LINE_HEIGHT_MULTIPLIER,
  })

const text_fits = (text: PIXI.Text, max_width: number, max_height: number): boolean =>
  text.width <= max_width && text.height <= max_height

const fit_full_title_text = (
  text: PIXI.Text,
  content: string,
  idea: Idea,
  ideaColourResolver: IdeaColourResolver,
  max_width: number,
  max_height: number,
): void => {
  let font_size = FULL_TITLE_FONT_SIZE

  while (!text_fits(text, max_width, max_height) && font_size - 0.5 >= FULL_TITLE_MIN_FONT_SIZE) {
    font_size -= 0.5
    text.style = full_title_text_style(idea, font_size, ideaColourResolver, max_width)
  }

  if (text_fits(text, max_width, max_height)) return

  let low = 0
  let high = content.length

  while (low < high) {
    const mid = Math.ceil((low + high) / 2)
    text.text = `${content.slice(0, mid).trimEnd()}${FULL_TITLE_ELLIPSIS}`

    if (text_fits(text, max_width, max_height)) low = mid
    else high = mid - 1
  }

  text.text =
    low < content.length ? `${content.slice(0, low).trimEnd()}${FULL_TITLE_ELLIPSIS}` : content
}

const create_full_title_text = (
  idea: Idea,
  ideaColourResolver: IdeaColourResolver,
  textureResolution: number,
  reservedTop: number,
): PIXI.Text | null => {
  const content = full_title_content(idea)
  if (!content) return null

  const max_width = BOX_WIDTH * ideaWidth(idea) - FULL_TITLE_PADDING * 2
  const max_height = BOX_WIDTH * ideaHeight(idea) - reservedTop - FULL_TITLE_PADDING * 2
  if (max_height <= 0) return null

  const text = new PIXI.Text(
    content,
    full_title_text_style(idea, FULL_TITLE_FONT_SIZE, ideaColourResolver, max_width),
  )
  text.resolution = textureResolution

  fit_full_title_text(text, content, idea, ideaColourResolver, max_width, max_height)
  text.cullable = true

  return text
}

const create_memo_marker_text = (
  idea: Idea,
  ideaColourResolver: IdeaColourResolver,
  textureResolution: number,
): PIXI.Text | null => {
  const content = memo_marker_content(idea)
  if (!content) return null

  const text = new PIXI.Text(
    content,
    new PIXI.TextStyle({
      fontSize: FULL_TITLE_MEMO_FONT_SIZE,
      fill: idea_text_colour(idea, ideaColourResolver).fill,
      align: "left",
    }),
  )
  text.resolution = textureResolution
  text.x = FULL_TITLE_MEMO_PADDING
  text.y = FULL_TITLE_MEMO_PADDING
  text.cullable = true

  return text
}

const renderFullTitleToGraphic = (
  graphic: PIXI.Container,
  idea: Idea,
  idTexts: Map<ID, PIXI.Container> | null,
  originX: number = 0,
  originY: number = 0,
  ideaColourResolver: IdeaColourResolver = defaultIdeaColourResolver,
  textureResolution: number = window.devicePixelRatio || 1,
): PIXI.Container | null => {
  const memoMarker = create_memo_marker_text(idea, ideaColourResolver, textureResolution)
  const reservedTop = memoMarker
    ? Math.ceil(memoMarker.y + memoMarker.height + FULL_TITLE_MARKER_TITLE_GAP)
    : 0
  const titleText = create_full_title_text(idea, ideaColourResolver, textureResolution, reservedTop)
  if (!titleText && !memoMarker) return null

  const container = new PIXI.Container()
  const width = BOX_WIDTH * ideaWidth(idea)
  const height = BOX_WIDTH * ideaHeight(idea)
  container.x = (idea.location.gx - originX) * BOX_WIDTH
  container.y = (idea.location.gy - originY) * BOX_WIDTH
  container.cullable = true

  if (titleText) {
    const titleAreaHeight = height - reservedTop
    titleText.x = (width - titleText.width) / 2
    titleText.y =
      reservedTop + (titleAreaHeight - titleText.height) * FULL_TITLE_VERTICAL_BIAS_BELOW_MARKER
    container.addChild(titleText)
  }

  if (memoMarker) container.addChild(memoMarker)

  if (idTexts) idTexts.set(idea.id, container)
  graphic.addChild(container)

  return container
}

const renderIdeaTextToGraphic = (
  graphic: PIXI.Container,
  idea: Idea,
  idTexts: Map<ID, PIXI.BitmapText | PIXI.Text> | null,
  originX: number = 0,
  originY: number = 0,
  ideaColourResolver: IdeaColourResolver = defaultIdeaColourResolver,
): PIXI.BitmapText | PIXI.Text | null => {
  const content = idea_text_content(idea)
  if (!content) return null

  const text = create_idea_text(idea, content, ideaColourResolver)
  fit_idea_text_to_cell(idea, text)
  text.cullable = true
  if (idTexts) idTexts.set(idea.id, text)
  setIdeaTextLocation(idea, text, originX, originY)
  graphic.addChild(text)

  return text
}

const setIdeaTextLocation = (
  idea: Idea,
  text: PIXI.BitmapText | PIXI.Text,
  originX: number,
  originY: number,
): void => {
  const gx = idea.location.gx - originX
  const gy = idea.location.gy - originY
  text.x = gx * BOX_WIDTH + (BOX_WIDTH * ideaWidth(idea) - text.width) / 2
  text.y = gy * BOX_WIDTH + (BOX_WIDTH * ideaHeight(idea) - text.height) / 2
}

const convert_css_colour_string_to_rgb = (colour: HexColor) => {
  const r = parseInt(colour.slice(1, 3), 16)
  const g = parseInt(colour.slice(3, 5), 16)
  const b = parseInt(colour.slice(5, 7), 16)
  return [r, g, b]
}

const text_should_be_white_or_blank = (bg_colour: HexColor) => {
  const [r, g, b] = convert_css_colour_string_to_rgb(bg_colour)
  const brightness = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b)
  return brightness < 140
}

const clearIdeaTextFromGraphic = <T extends PIXI.DisplayObject>(
  graphic: PIXI.Container,
  idea: Idea,
  idTexts: Map<ID, T>,
): void => {
  const text = idTexts.get(idea.id)
  const children = [...graphic.children]
  children.forEach((child) => {
    if (child === text) {
      graphic.removeChild(child)
      child.destroy({ children: true })
    }
  })
  idTexts.delete(idea.id)
}

export {
  clearIdeaTextFromGraphic,
  full_title_content,
  renderFullTitleToGraphic,
  renderIdeaTextToGraphic,
  setIdeaTextLocation,
}
